import { Injectable } from '@angular/core';
import { PlatformService } from './platform.service';
import { FileService } from './file.service';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FilePicker } from '@capawesome/capacitor-file-picker';

@Injectable({
  providedIn: 'root'
})
export class DocumentPickerCapacitorService {

  constructor(
    private platformService: PlatformService,
    private fileService: FileService
  ) {}

  /**
   * Selecciona un documento usando @capacitor/file-transfer
   * @returns Promise con la información del documento seleccionado
   */
  public async pickDocument(fileInput?: any): Promise<any> {
    try {
      if (this.platformService.isWeb()) {
        // En web, usar input file nativo
        return await this.pickDocumentWeb();
      } else if (this.platformService.isMobile()) {
        // En móvil, usar directamente el plugin Share
        return await this.pickDocumentWithShare();
      }
    } catch (error) {
      console.error('Error picking document:', error);
      throw error;
    }
  }

  /**
   * Selecciona documento en web usando input file
   */
  private async pickDocumentWeb(): Promise<any> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '*/*';
      input.multiple = false;

      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
          try {
            const processedFile = await this.fileService.generateDocumentFile(file);
            resolve({
              document: processedFile,
              success: true
            });
          } catch (error) {
            console.error('Error processing file:', error);
            reject(error);
          }
        } else {
          reject(new Error('No file selected'));
        }
      };

      input.onerror = () => {
        console.error('File selection failed');
        reject(new Error('File selection failed'));
      };

      input.click();
    });
  }

  /**
   * Selecciona documento en móvil usando APIs nativas de Capacitor
   */
  private async pickDocumentMobile(fileInput?: any): Promise<any> {
    try {
      if (fileInput && fileInput.nativeElement) {
        console.log('Using existing file input for mobile');
        return await this.useExistingFileInput(fileInput);
      } else {
        console.log('No file input provided, using dynamic picker');
        // Para móvil, usar directamente las APIs nativas sin input file
        const result = await this.showMobileDocumentPicker();
        return {
          document: result,
          success: true
        };
      }
    } catch (error) {
      console.error('Error picking document on mobile:', error);
      throw error;
    }
  }

  /**
   * Usa el input file existente en el HTML
   */
  private async useExistingFileInput(fileInput: any): Promise<any> {
    try {
      console.log('useExistingFileInput() called');
      
      // En iOS, vamos a intentar un enfoque diferente usando un input visible
      return new Promise((resolve, reject) => {
        const input = fileInput.nativeElement;
        
        // Configurar el input para documentos
        input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,*/*';
        input.multiple = false;
        
        // Hacer el input visible pero muy pequeño para iOS
        input.style.position = 'fixed';
        input.style.top = '50%';
        input.style.left = '50%';
        input.style.transform = 'translate(-50%, -50%)';
        input.style.width = '1px';
        input.style.height = '1px';
        input.style.opacity = '0.01';
        input.style.zIndex = '9999';
        
        // Crear un listener temporal
        const handleFileChange = async (event: any) => {
          console.log('Existing file input selected:', event.target.files[0]);
          const file = event.target.files[0];
          if (file) {
            try {
              console.log('Processing existing file with fileService.generateDocumentFile()');
              const processedFile = await this.fileService.generateDocumentFile(file);
              console.log('Existing file processed successfully:', processedFile);
              resolve(processedFile);
            } catch (error) {
              console.error('Error processing existing file:', error);
              reject(error);
            }
          } else {
            console.log('No existing file selected');
            reject(new Error('No existing file selected'));
          }
          
          // Limpiar el listener y restaurar el estilo
          input.removeEventListener('change', handleFileChange);
          input.style.position = '';
          input.style.top = '';
          input.style.left = '';
          input.style.transform = '';
          input.style.width = '';
          input.style.height = '';
          input.style.opacity = '';
          input.style.zIndex = '';
        };

        // Agregar el listener
        input.addEventListener('change', handleFileChange);
        
        // Trigger el click con un timeout para iOS
        setTimeout(() => {
          console.log('Triggering existing file input click with timeout');
          input.click();
        }, 100);
      });
    } catch (error) {
      console.error('Error using existing file input:', error);
      throw error;
    }
  }

  /**
   * Usa el plugin FilePicker para seleccionar documentos
   */
  private async pickDocumentWithShare(): Promise<any> {
    try {
      // Usar el plugin FilePicker real
      const result = await FilePicker.pickFiles({
        readData: true
      });
      
      if (result.files && result.files.length > 0) {
        const file = result.files[0];
        
        // Convertir base64 a ArrayBuffer para crear un Blob binario
        const base64Data = file.data;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Crear un Blob con los datos binarios
        const blob = new Blob([bytes], { type: file.mimeType });
        
        const document = {
          name: file.name,
          type: file.mimeType,
          size: file.size,
          lastModified: file.modifiedAt,
          blob: blob,
          originalData: base64Data // Guardar los datos base64 originales
        };
        
        return {
          document: document,
          success: true
        };
      } else {
        throw new Error('No document selected');
      }
    } catch (error) {
      console.error('Error with FilePicker plugin:', error);
      throw error;
    }
  }

  /**
   * Crea un archivo temporal para el plugin Share
   */
  private async createTempFile(): Promise<string> {
    try {
      // Crear un archivo temporal en el sistema de archivos
      const tempContent = 'Temporary file for document picker';
      const fileName = `temp_${Date.now()}.txt`;
      
      await Filesystem.writeFile({
        path: fileName,
        data: tempContent,
        directory: Directory.Cache
      });
      
      return fileName;
    } catch (error) {
      console.error('Error creating temp file:', error);
      throw error;
    }
  }

  /**
   * Muestra el selector de documentos móvil usando APIs nativas
   */
  private async showMobileDocumentPicker(): Promise<any> {
    try {
      console.log('showMobileDocumentPicker() called - using iOS compatible file picker');
      
      // En iOS, necesitamos un enfoque diferente para que funcione el selector
      return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,*/*';
        input.multiple = false;
        
        // En iOS, necesitamos hacer el input visible pero muy pequeño
        input.style.position = 'fixed';
        input.style.top = '0';
        input.style.left = '0';
        input.style.width = '1px';
        input.style.height = '1px';
        input.style.opacity = '0.01';
        input.style.zIndex = '-1';
        
        input.onchange = async (event: any) => {
          console.log('iOS file selected:', event.target.files[0]);
          const file = event.target.files[0];
          if (file) {
            try {
              console.log('Processing iOS file with fileService.generateDocumentFile()');
              const processedFile = await this.fileService.generateDocumentFile(file);
              console.log('iOS file processed successfully:', processedFile);
              resolve(processedFile);
            } catch (error) {
              console.error('Error processing iOS file:', error);
              reject(error);
            }
          } else {
            console.log('No iOS file selected');
            reject(new Error('No iOS file selected'));
          }
          
          // Limpiar el input después de usarlo
          setTimeout(() => {
            if (document.body.contains(input)) {
              document.body.removeChild(input);
            }
          }, 100);
        };

        input.onerror = () => {
          console.error('iOS file selection failed');
          setTimeout(() => {
            if (document.body.contains(input)) {
              document.body.removeChild(input);
            }
          }, 100);
          reject(new Error('iOS file selection failed'));
        };

        // Agregar el input al DOM y usar un timeout para asegurar que se renderice
        document.body.appendChild(input);
        
        // Usar un timeout para dar tiempo a que iOS procese el input
        setTimeout(() => {
          console.log('Triggering iOS file input click');
          input.click();
        }, 100);
      });
    } catch (error) {
      console.error('Error in mobile document picker:', error);
      throw error;
    }
  }


} 